import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  Dropdown,
  DropdownButton,
  Button,
} from 'react-bootstrap';

import { useParams } from 'react-router-dom';
import { teamApi } from '../../api';
import { hasPermissions, hasExpired, formatRole, formatRoleID } from '../../utils';
import { useInfiniteScroll } from '../../utils/hooks';

import PageTitle from '../../components/common/PageTitle';
import Loader from '../../components/common/Loader';
import ConfirmModal from '../../components/common/ConfirmModal';
import Member from '../../components/team/Member';
import MemberDataModal from '../../components/team/MemberDataModal';

import { useAuth } from '../../components/context';

function Members({ t, user, env, setAlert }) {
  const { action } = useParams();
  const { onUpdateUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [currentMember, setCurrentMember] = useState({
    show: false,
    delete: false,
    data: undefined,
    loading: false,
  });
  const [filters, setFilters] = useState({ role: '', search: '' });
  const { data, setData, setRefresh, lastItem, hasMore, isLoading, isFetching, loaded, error } =
    useInfiniteScroll(
      teamApi.fetchMembers,
      {
        slug: 'members',
        team: user.currentTeam ? user.currentTeam.id : null,
        limit: 20,
      },
      hasPermissions(user, 'members', 'Read')
    );

  const members = data.filter((member) => {
    const { role, search } = filters;

    if (role) {
      const hasMatchingRole =
        (member.role && member.role.id === role) ||
        (member.invitation && formatRoleID(member.invitation.role) === role);

      if (!hasMatchingRole) {
        return false;
      }
    }

    if (search) {
      const lowerSearch = search.toLowerCase();
      const matchedName = member.member?.name?.toLowerCase().includes(lowerSearch);
      const matchedHandle = member.member?.handle?.toLowerCase().includes(lowerSearch);
      const matchedMemberEmail = member.member?.email?.toLowerCase().includes(lowerSearch);
      const matchedInvitationEmail = member.invitation?.email?.toLowerCase().includes(lowerSearch);

      if (![matchedName, matchedHandle, matchedMemberEmail, matchedInvitationEmail].some(Boolean)) {
        return false;
      }
    }

    return true;
  });

  const handleCreate = async (values) => {
    if (values && ((user.role !== 1 && parseInt(values.role.id, 10) !== 1) || user.role === 1)) {
      setCurrentMember({ ...currentMember, loading: true });
      try {
        const response = await teamApi.inviteMember(values, user.currentTeam, env);
        setAlert({ message: response.message, variant: 'success' });
        setRefresh(true);
        if (!user.hasMembers) onUpdateUser({ hasMembers: true });
      } catch (err) {
        setAlert({ message: err, variant: 'warning' });
      } finally {
        setCurrentMember({ ...currentMember, data: undefined, show: false });
      }
    } else {
      setCurrentMember({ ...currentMember, data: undefined, show: false });
    }
  };

  const handleEdit = async (values) => {
    if (values.invitation) {
      // eslint-disable-next-line no-param-reassign
      values.role = { id: formatRoleID(values.invitation.role), name: values.invitation.role };
      setCurrentMember({ ...currentMember, data: values, show: true });
    } else {
      setCurrentMember({ ...currentMember, data: { member: {} }, show: true, loading: true });
      try {
        const response = await teamApi.getMember(values, user.currentTeam);
        setCurrentMember({ ...currentMember, data: response, show: true, loading: false });
      } catch (err) {
        setAlert({ message: err, variant: 'warning' });
      }
    }
  };

  const handleSave = async (values) => {
    if (
      values &&
      values.member &&
      !values.member.token &&
      ((user.role !== 1 && parseInt(values.role.id, 10) !== 1) || user.role === 1)
    ) {
      try {
        const response = await teamApi.updateMember(values, user.currentTeam);
        setAlert({ message: response.message, variant: 'success' });
        setData(
          data.map((m) =>
            m.member &&
            m.member.id &&
            values.member &&
            values.member.id &&
            m.member.id === values.member.id
              ? { member: values.member, role: values.role }
              : m
          )
        );
      } catch (err) {
        setAlert({ message: err, variant: 'warning' });
      } finally {
        setCurrentMember({ ...currentMember, data: undefined, show: false });
      }
    } else if (values && values.invitation) {
      // eslint-disable-next-line no-use-before-define
      handleRevoke(values);
      handleCreate(values);
      setCurrentMember({ ...currentMember, data: undefined, show: false });
    } else {
      setCurrentMember({ ...currentMember, data: undefined, show: false });
    }
  };

  const handleRevoke = async (values) => {
    if (values.invitation) {
      try {
        await teamApi.deleteInvitation(values.invitation.id);
        setData(
          data.filter((m) => m.member || (m.invitation && m.invitation.id !== values.invitation.id))
        );
        if (!values.member)
          setAlert({
            message: t('settings.members.messages.delete', { user: values.invitation.email }),
            variant: 'success',
          });
      } catch (err) {
        setAlert({ message: err, variant: 'warning' });
      }
    }
  };

  const handleDelete = async (values) => {
    if (values && values.member) {
      try {
        await teamApi.deleteMember(values, user.currentTeam, user.entity.handle);
        setData(data.filter((m) => m.member && m.member.id !== values.member.id));
        setAlert({
          message: t('settings.members.messages.delete', { user: values.member.name }),
          variant: 'success',
        });
      } catch (err) {
        setAlert({ message: err, variant: 'warning' });
      } finally {
        setCurrentMember({ ...currentMember, data: undefined, delete: false });
      }
    } else if (values && values.invitation) {
      handleRevoke(values);
      setCurrentMember({ ...currentMember, data: undefined, delete: false });
    } else {
      setCurrentMember({ ...currentMember, data: undefined, delete: false });
    }
  };

  useEffect(() => {
    if (error) setAlert({ message: error, variant: 'warning' });
  }, [error]);

  useEffect(() => {
    if (loaded) setRefresh(true);
  }, [user.currentTeam]);

  useEffect(() => {
    setRefresh(true);
  }, []);

  useEffect(() => {
    members.forEach((member) => {
      if (
        member.role &&
        !roles.some((role) => parseInt(role, 10) === parseInt(member.role.id, 10))
      ) {
        setRoles([...roles, member.role.id]);
      }
      if (
        member.invitation &&
        !roles.some((role) => parseInt(role, 10) === formatRoleID(member.invitation.role))
      ) {
        setRoles([...roles, formatRoleID(member.invitation.role)]);
      }
    });
  }, [members]);

  useEffect(() => {
    if (action === 'new' && hasPermissions(user, 'members', 'Write')) {
      setCurrentMember({ ...currentMember, data: undefined, show: true });
    }
  }, []);

  const compareMembersAndInvitations = (x, y) => {
    if (x.member && y.member) {
      return x.member.name.localeCompare(y.member.name);
    }
    if (x.invitation && y.invitation) {
      return x.invitation.email.localeCompare(y.invitation.email);
    }
    if (x.member) {
      return -1;
    }
    if (y.member) {
      return 1;
    }
    if (x.invitation && !hasExpired(x.invitation.expires_at)) {
      return -1;
    }
    if (y.invitation && !hasExpired(y.invitation.expires_at)) {
      return 1;
    }
    return 0;
  };

  return (
    <Container className="main-content-container members p-3 p-md-5">
      <Row className="g-0 page-header mb-3 mb-md-5">
        <PageTitle title={t('settings.members.title')} backBtn />
      </Row>

      {isLoading ? (
        <Loader />
      ) : (
        <Card className="mb-3 mb-md-5">
          <Card.Header as={Row} className="p-4 g-0">
            <Col sm="8" className="roles d-block d-sm-flex">
              <Form.Control
                size="lg"
                className="w-100"
                placeholder={t('common:labels.search')}
                aria-label={t('common:labels.search')}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.currentTarget.value.trimStart() })
                }
              />
              {roles.length > 0 && (
                <DropdownButton
                  className="text-nowrap ms-0 mt-2 ms-sm-2 mt-sm-0"
                  variant="outline-light"
                  title={
                    filters.role ? formatRole(t, filters.role) : t('settings.members.labels.all')
                  }
                >
                  {roles
                    .filter((role) => filters.role !== role)
                    .map((role) => (
                      <Dropdown.Item
                        key={role}
                        onClick={() => setFilters({ ...filters, role: role })}
                      >
                        {formatRole(t, role)}
                      </Dropdown.Item>
                    ))}
                  {filters.role && (
                    <Dropdown.Item onClick={() => setFilters({ ...filters, role: '' })}>
                      {t('settings.members.labels.all')}
                    </Dropdown.Item>
                  )}
                </DropdownButton>
              )}
            </Col>
            <Col sm="4" className="text-end">
              {hasPermissions(user, 'members', 'Write') && (
                <Button
                  variant="outline-light"
                  onClick={() => {
                    setCurrentMember({ ...currentMember, data: undefined, show: true });
                  }}
                >
                  {t('settings.members.buttons.create')} <i className="fas fa-plus-circle ms-2" />
                </Button>
              )}
            </Col>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="mx-n4">
              <Table className="border-bottom" hover responsive>
                <thead>
                  <tr>
                    <th className="ps-4">{t('settings.members.labels.name')}</th>
                    {members[0] && members[0].member && members[0].member.email ? (
                      <th>{t('settings.members.labels.email')}</th>
                    ) : (
                      <th>{t('settings.members.labels.handle')}</th>
                    )}
                    <th>{t('settings.members.labels.role')}</th>
                    <th>{t('settings.members.labels.status')}</th>
                    {hasPermissions(user, 'members', 'Delete') && (
                      <th width="1%">
                        <span className="sr-only">{t('common:buttons.delete')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!members.length > 0 && (
                    <tr className="disabled">
                      <td colSpan="5" className="p-0 cursor-default">
                        <p className="text-center text-info font-italic mx-4 my-5">
                          {t(`settings.members.empty.${filters.search ? 'search' : 'entries'}`)}
                        </p>
                      </td>
                    </tr>
                  )}
                  {members.length > 0 &&
                    members.sort(compareMembersAndInvitations).map((member, key) => {
                      return (
                        <Member
                          // eslint-disable-next-line react/no-array-index-key
                          key={key}
                          data={member}
                          onEdit={handleEdit}
                          onDelete={() => {
                            setCurrentMember({ ...currentMember, data: member, delete: true });
                          }}
                          ref={members[members.length - 1].key === members.key ? lastItem : null}
                        />
                      );
                    })}
                </tbody>
              </Table>
            </div>
            {loaded && isFetching && hasMore && (
              <div className="fetching">
                <Loader label={t('settings.members.loading')} />
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {currentMember.show && (
        <MemberDataModal
          show={currentMember.show}
          onHide={currentMember.show && currentMember.data ? handleSave : handleCreate}
          data={currentMember.data}
          loading={currentMember.loading}
        />
      )}

      {currentMember.delete && (
        <ConfirmModal
          show={currentMember.delete}
          data={currentMember.data}
          title={t('common:buttons.confirm')}
          message={
            currentMember.data.invitation
              ? t('settings.members.messages.revoke', {
                  email: currentMember.data.invitation.email,
                })
              : t('common:form.messages.confirm.delete', {
                  name: currentMember.data.member
                    ? currentMember.data.member.name
                    : currentMember.data.invitation.email,
                })
          }
          buttonLabel={
            currentMember.data.invitation
              ? t('settings.members.buttons.revoke')
              : t('common:buttons.delete')
          }
          buttonVariant="warning"
          onHide={handleDelete}
        />
      )}
    </Container>
  );
}

export default Members;
